import React from 'react';
import { injectIntl } from 'react-intl';
import {
  BellRing,
  House,
  LifeBuoy,
  Mail,
  ChevronDown,
  Compass,
  Mic,
  NotebookPen,
  Radar,
  ChevronRight,
  Pencil,
  Plus,
  Lightbulb,
  Square,
  SquareCheck
} from 'lucide-react';
import {
  avatarGenderClass,
  clientIdentityOf,
  cpiOf,
  elapsedOf,
  trackingOf,
  familiesOf,
  isRecruitmentOf,
  reminderMarkOf,
  tierMetaOf
} from '../clientPresentation';
import { clientCapabilitiesOf, clientRestrictionReasonOf } from '../clientEntitlements';
import RecruitStageDot from './RecruitStageDot';
import { RECRUIT_STAGE_COLORS, useRecruitStage } from '../recruitStage';
import { useCanEditClientNotes } from '../clientNotesStore';
import { RECRUIT_AXES, summariseRecruitScores, useRecruitScores } from '../recruitScore';
import type { IntlLike } from '../clientPresentation';
import type { ClientCapability } from '../clientEntitlements';
import { msgOptOf } from '../clientLegacyState';
import type { ClientDrawerAction } from '../clientActions';
import type { QueueClient } from '../../../../types/client';

export type ClientRowProps = {
  client: QueueClient;
  selected: boolean;
  onSelect: (clientId: string) => void;
  /**
   * V1's family label applies that client's complete family set as the list
   * filter; clicking the same set again clears it (ClientEntry.js:749-767).
   */
  onFamilyFilter?: (families: string[]) => void;
  /**
   * Forwarded to qualifyingFactsheetOf, same as ClientHealthCard — without it,
   * a client shared by multiple advisers resolves against the FIRST
   * advisorDetails entry, which can belong to someone else.
   */
  adviserEmail?: string;
  selectionMode?: 'ClientToSend';
  onSelectionToggle?: (client: QueueClient) => void;
  /**
   * Per-row mail affordance V1 renders as `summaryPrepend` in certain list
   * states (clientPage.js:2521-2566): birthday states open the greeting dialog,
   * reminder states open the meeting note. Absent in every other state.
   */
  mailAction?: 'birthday' | 'meeting-note';
  onMail?: (client: QueueClient) => void;
  /**
   * Move-policy target selector. V1 renders its checkbox ONLY on eligible rows
   * (ClientEntry.js:724-726) — ineligible rows simply get nothing — and the row
   * itself keeps behaving normally, so the caller decides eligibility.
   */
  moveSelectable?: boolean;
  onMoveSelect?: (client: QueueClient) => void;
  /**
   * The row's three shortcuts: 救命紙, 會面筆記, 智能摘要. Rendered only when the
   * host can dispatch the action — leaves the row without these affordances in
   * tests/story states that have no dialog plumbing.
   *
   * These used to be V1's set (contact dialog, portfolio donut, smart idea —
   * ClientEntry.js:796-817). None of the first two was among the features the
   * business promotes, so the fastest path in the whole list surfaced none of
   * them; 聯絡 and 保單組合 are one tap away inside the drawer instead. The
   * connection colour the portfolio donut carried is not relocated: the drawer's
   * connection pill states the same thing in words.
   */
  onQuickAction?: (action: ClientDrawerAction, client: QueueClient) => void;
  /**
   * 招募模式. The three shortcuts become the recruitment set — 招募 IDEA, 招募
   * 筆記, 候選人評估 — because in this mode the row is a candidate, and 救命紙 /
   * 智能摘要 are about a policy holder.
   *
   * Only the middle one is a dispatchable action: 招募筆記 IS the meeting-note
   * dialog (design doc §4.2 — one room, one door, a clearer label on it). The
   * other two are readouts the drawer mounts, so they travel by
   * `onRecruitSurface` instead.
   */
  recruitMode?: boolean;
  onRecruitSurface?: (client: QueueClient, surface: 'idea' | 'score' | 'quiz' | 'drawer') => void;
  /**
   * Opens the stage menu from the row's 階段 pill. The pill renders only when
   * this is given: a pill that showed the stage but could not change it would
   * be a control that isn't one, and the row has no menu of its own.
   */
  onRecruitStage?: (client: QueueClient, anchor: HTMLElement) => void;
  /**
   * Called instead of `onQuickAction` when the client's connection status
   * withholds the shortcut (clientEntitlements.ts). The host announces it; this
   * leaf only reports that a refusal happened. Matters most here: a row has no
   * banner to explain itself, and `title` never shows on touch.
   */
  onRestricted?: (client: QueueClient, capability: ClientCapability) => void;
  /**
   * Opens the C.P.I. explainer from the row's potential dot. Same handler the
   * command bar's client-base indicator and the drawer's 潛力 tag use, so all
   * three entrances open one dialog. Without it the dot renders as a plain
   * aria-hidden fact, as it did before it became a control.
   */
  onCpiInfo?: () => void;
  /**
   * New-client spotlight (component.tsx): this row's id just appeared in a
   * refreshed clientData, so the row flashes the page accent once to answer
   * "where did it land". Pure decoration — no behaviour rides on it.
   */
  spotlight?: boolean;
  intl: IntlLike;
};

const MAIL_LABEL_KEY: Record<'birthday' | 'meeting-note', string> = {
  birthday: 'Greeting',
  'meeting-note': 'meetingNotes'
};

/**
 * The `summaryPrepend` checkbox V1 renders for the ClientToSend and move-policy
 * flows (ClientEntry.js:713-734). V1 uses a real MUI `<Checkbox>` — focusable,
 * announced, toggled with Space — so this reproduces those semantics with
 * `role="checkbox"` + `aria-checked` rather than the inert `aria-hidden` span it
 * replaces. A native `<input>` is not an option: the row is itself a `<button>`,
 * whose content model forbids interactive descendants.
 *
 * Space only, matching the native checkbox V1 uses (Enter does not toggle one).
 */
const RowCheckbox = (props: {
  checked: boolean;
  label: string;
  testId: string;
  onToggle: () => void;
}): React.ReactElement => (
  <span
    role="checkbox"
    tabIndex={0}
    aria-checked={props.checked}
    aria-label={props.label}
    title={props.label}
    data-testid={props.testId}
    data-checked={props.checked ? 'true' : 'false'}
    className="clientv2-row-checkbox"
    onClick={event => {
      event.stopPropagation();
      props.onToggle();
    }}
    onKeyDown={event => {
      if (event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      props.onToggle();
    }}
  >
    {props.checked ? (
      <SquareCheck size={20} color="#3966F8" className="clientv2-row-checkbox-icon" aria-hidden="true" />
    ) : (
      <Square size={20} color="#ccc" className="clientv2-row-checkbox-icon" aria-hidden="true" />
    )}
  </span>
);

/**
 * One of V1's collapsed-row shortcut icons. Same pattern as the row's mail
 * control and checkboxes: the row itself is a <button>, so these are focusable
 * role="button" spans (invalid HTML aside, a nested <button> would also make
 * the row's single-activation-path guarantee untestable). A disabled shortcut
 * (the disconnected portfolio donut) stays visible but inert, like V1's grey
 * doughnut_chart_grey.svg.
 *
 * "Inert" means it never runs the action — but it does report the refusal
 * through `onRefuse` when there is one, so the host can say why. `title` cannot
 * do that job on touch, and the row (unlike the drawer) has no banner to fall
 * back on.
 *
 * `corner` is the badge that marks a shortcut which lands DEEPER than the
 * drawer card wearing the same icon and name — ✏️ on 救命紙 (the sheet's editor,
 * not the export menu), 🎤 on 會面筆記 (the recorder, not the note form). One
 * rule, learnable from one look at a row: a badged icon skips a step, an
 * unbadged one (智能摘要) is the drawer's card exactly. Without it the two
 * entrances make the same promise in the same words and keep it differently.
 */
const RowShortcut = (props: {
  testId: string;
  label: string;
  /** Tooltip when the shortcut is disabled — why, rather than what. */
  hint?: string;
  disabled?: boolean;
  /** Called instead of onActivate while disabled, if the host wants to explain. */
  onRefuse?: () => void;
  /** Marks a shortcut that enters one step further in than the drawer's card. */
  corner?: React.ReactNode;
  className?: string;
  onActivate: () => void;
  children: React.ReactNode;
}): React.ReactElement => (
  <span
    role="button"
    tabIndex={0}
    data-testid={props.testId}
    className={`clientv2-row-shortcut${props.className ? ` ${props.className}` : ''}${
      props.disabled ? ' is-disabled' : ''
    }`}
    // The accessible name stays the ACTION even when it is unavailable — an icon
    // that announces itself as a refusal is unidentifiable. `aria-disabled` says
    // it cannot be used; the title says why.
    aria-label={props.label}
    aria-disabled={props.disabled || undefined}
    title={(props.disabled && props.hint) || props.label}
    onClick={event => {
      event.stopPropagation();
      if (props.disabled) props.onRefuse?.();
      else props.onActivate();
    }}
    onKeyDown={event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      if (props.disabled) props.onRefuse?.();
      else props.onActivate();
    }}
  >
    {props.children}
    {props.corner && (
      // Decorative: the accessible name already carries the same distinction in
      // words ("救命紙 · 修改"), so announcing the badge as well would say it twice.
      <span aria-hidden="true" className="clientv2-row-shortcut-corner">
        {props.corner}
      </span>
    )}
  </span>
);

/**
 * One row of the client list. Real <button>, not a div+onClick: a native button
 * is focusable and normalises Enter/Space into one 'click', so there is exactly
 * one activation path. Selection is aria-current="true" (not aria-pressed — the
 * row doesn't toggle its own state, it opens a drawer for that client), which
 * also drives the CSS ring via `&[aria-current='true']`.
 *
 * v46 look: gender-coloured avatar, name (+nickname), recruitment, tier badge,
 * family chip, time-since-meeting dot, CPI dot, 跟進中 chip. Coverage and tags
 * were removed from the row (they live in the drawer). Every visible child is
 * aria-hidden; the button carries one composite aria-label of the row's facts.
 */
const ClientRow = (props: ClientRowProps) => {
  const {
    client,
    selected,
    onSelect,
    onFamilyFilter,
    adviserEmail,
    selectionMode,
    onSelectionToggle,
    mailAction,
    onMail,
    moveSelectable,
    onMoveSelect,
    onQuickAction,
    recruitMode,
    onRecruitSurface,
    onRecruitStage,
    onRestricted,
    onCpiInfo,
    spotlight,
    intl
  } = props;
  // V1 renders the name and nickname as two nodes; `secondary` is empty unless a
  // real name came first, so a nickname-only client shows it once (see
  // clientIdentityOf). '-' only when there is nothing at all to show.
  const identity = clientIdentityOf(client);
  const name = identity.primary || '-';
  const nickname = identity.secondary;
  const tier = tierMetaOf(client, adviserEmail);
  const tierLabel = tier ? intl.formatMessage({ id: tier.key }) : '';
  const recruiting = isRecruitmentOf(client, adviserEmail);
  // One subscription per row, and only rows in 招募模式 ever read it: a write
  // from the drawer, 快速資料 or this pill's own menu lands here directly.
  const stage = useRecruitStage(recruitMode ? client._id : null);
  // No factsheet, nowhere to save a stage: the pill still shows it but is
  // disabled, like the drawer header's.
  const canSaveStage = useCanEditClientNotes(recruitMode ? client._id : null);
  // 招募模式下取代 C.P.I. 的那一顆(見下面的 row-recruit-score 晶片)。同樣只有
  // 招募列會訂閱。
  const recruitScores = useRecruitScores(recruitMode ? client._id : null);
  const recruitScore = recruitMode ? summariseRecruitScores(recruitScores) : null;
  // 晶片上寫的是「評估」,不是分數 —— 和旁邊那顆 C.P.I. 同一種讀法:一個標籤加
  // 一點顏色,三段式的紅綠燈。列是用掃的,「72 分」要先換算成好或不好才有意思,
  // 而在這裡要的只是那個結論。分數本身沒有不見,它在 title、讀屏標籤,以及按下
  // 去就打開的那張評分表裡。
  const recruitScoreState: 'priority' | 'nurture' | 'hold' | 'partial' | 'unrated' = !recruitScore
    ? 'unrated'
    : recruitScore.tier || (recruitScore.rated > 0 ? 'partial' : 'unrated');
  // 三種狀態、三句既有的話:評齊了報總分和分段,評了一半說評到哪,一項都沒有就
  // 說「按此評分」—— 一顆按得下去的晶片,空著的時候講的應該是怎麼把它填滿。
  const recruitScoreDetail = !recruitScore
    ? ''
    : recruitScore.total !== null
    ? `${intl.formatMessage({ id: `clientv2-recruit-tier-${recruitScore.tier}` })} · ${intl.formatMessage(
        { id: 'clientv2-recruit-total-points' },
        { score: recruitScore.total }
      )}`
    : recruitScore.rated > 0
    ? intl.formatMessage(
        { id: 'clientv2-recruit-total-partial' },
        { rated: recruitScore.rated, count: RECRUIT_AXES.length }
      )
    : intl.formatMessage({ id: 'clientv2-recruit-radar-empty' });
  // 未評過的那一顆仍然自己說話:一顆只寫著「評估」的空晶片,看起來像已經有答案。
  const recruitScoreLabel =
    recruitScoreState === 'unrated'
      ? intl.formatMessage({ id: 'clientv2-recruit-radar-empty' })
      : intl.formatMessage({ id: 'clientv2-recruit-row-score' });
  const families = familiesOf(client, adviserEmail);
  const cpi = cpiOf(client, intl);
  const elapsed = elapsedOf(client, intl, adviserEmail);
  const remindMark = reminderMarkOf(client, adviserEmail);
  // The lead button goes red only when it is the REMINDER's button and that
  // reminder is due or past. 'birthday' wires the same control to the greeting
  // flow, where a lapsed reminder is not what the tap acts on.
  const mailDue = mailAction === 'meeting-note' && remindMark === 'due';
  const tracking = trackingOf(client);
  // Same per-status rules the drawer obeys (clientEntitlements.ts): the row's
  // shortcuts are the same actions, so they cannot be more permissive than the
  // tiles behind them.
  const capabilities = clientCapabilitiesOf(client);
  const restrictionKey = clientRestrictionReasonOf(client, 'portfolio');
  const restrictionText = restrictionKey ? intl.formatMessage({ id: restrictionKey }) : undefined;
  const potentialWord = intl.formatMessage({ id: 'clientv2-potential' });
  // The dot shows a colour and nothing else, so its name has to carry the band
  // in words — same two-part form as the command bar's indicator: the reading's
  // name, then this row's value.
  const cpiLabel = cpi ? `${intl.formatMessage({ id: 'clientv2-cpi-label' })} · ${cpi.wording}` : '';
  const cpiTitle = cpi ? `${intl.formatMessage({ id: 'Client_Potential_Index' })} · ${cpi.wording}` : '';
  // Just 跟進中, on one line, as V1's chip reads. The opportunity band used to be
  // stacked underneath it as a second line (High/Medium/Low), which made the chip
  // two rows tall for the only clients it appears on; the band still drives the
  // chip's colour and is editable as the 機率 L/M/H control in the drawer.
  const followingWord = intl.formatMessage({ id: 'Track Biz On' });

  const familyLabel =
    families.length === 1 ? families[0] : intl.formatMessage({ id: 'families-count' }, { count: families.length });

  // One composite label per row: identity first, then the scannable facts, so a
  // screen reader announces the same information the visual chips carry.
  const spokenName = identity.display || name;
  const labelParts: string[] = [tierLabel ? `${spokenName}, ${tierLabel}` : spokenName];
  if (cpi) labelParts.push(`${potentialWord} ${cpi.wording}`);
  if (elapsed) labelParts.push(elapsed.label);
  if (tracking) labelParts.push(followingWord);

  return (
    <button
      type="button"
      data-testid="client-row"
      // The page's FLIP reorder effect matches rows across renders by this id.
      data-client-id={client._id}
      // `has-lead` tells the phone grid to keep a column for the mail button or
      // the selection checkbox. Those render before the avatar and only in
      // those modes, and an always-present empty column would indent every
      // ordinary row by a gap it does not need.
      className={`clientv2-row${mailAction || selectionMode === 'ClientToSend' || moveSelectable ? ' has-lead' : ''}${
        spotlight ? ' is-spotlight' : ''
      }`}
      aria-current={selected ? 'true' : undefined}
      aria-label={labelParts.join('. ')}
      onClick={() => onSelect(client._id)}
    >
      {mailAction && (
        // The row itself is the <button>, so a nested <button> would be invalid
        // content. A focusable span with role="button" keeps the control
        // reachable by keyboard and announced, which V1's icon-only
        // ClientEntryMailButton never was (it carries no accessible name).
        <span
          role="button"
          tabIndex={0}
          data-testid="row-mail"
          data-mail-action={mailAction}
          // Red when this client's reminder is due or past — the avatar's mark
          // and this button read the same `remindMark`, so the row states its
          // urgency once, in two places, and never two different ways.
          //
          // Only on the reminder list's own button. The birthday list shows this
          // same control wired to the greeting flow (component.tsx rowMailAction),
          // and its predicate filters on dob alone — so a client with a birthday
          // today AND a lapsed reminder was getting a red 「祝賀」 button, which
          // says the greeting is late about a reminder the button does not touch.
          data-mail-due={mailDue ? 'true' : undefined}
          className={`clientv2-row-mail${mailDue ? ' is-due' : ''}`}
          aria-label={intl.formatMessage({ id: MAIL_LABEL_KEY[mailAction] })}
          title={intl.formatMessage({ id: MAIL_LABEL_KEY[mailAction] })}
          onClick={event => {
            event.stopPropagation();
            onMail?.(client);
          }}
          onKeyDown={event => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onMail?.(client);
          }}
        >
          {mailAction === 'meeting-note' ? (
            <BellRing size={15} aria-hidden="true" />
          ) : (
            <Mail size={15} aria-hidden="true" />
          )}
        </span>
      )}
      {selectionMode === 'ClientToSend' && (
        <RowCheckbox
          testId="row-msgopt-checkbox"
          checked={msgOptOf(client, adviserEmail)}
          label={intl.formatMessage({ id: 'clients-to-send' })}
          onToggle={() => onSelectionToggle?.(client)}
        />
      )}
      {moveSelectable && (
        // V1's move-policy checkbox is uncontrolled: it selects a target, it does
        // not represent stored state, so it always renders unchecked.
        <RowCheckbox
          testId="row-move-policy-checkbox"
          checked={false}
          label={intl.formatMessage({ id: 'Move Policy' })}
          onToggle={() => onMoveSelect?.(client)}
        />
      )}
      {/*
       * The VIP/VVIP tag rides on the avatar circle (bottom-right pill), not
       * the name line - the importance icon is part of the client's identity
       * in V1 too (ClientEntry renders it next to the avatar area), and the
       * name line stays for actual identity chips.
       */}
      <span aria-hidden="true" className="clientv2-row-avatar-wrap">
        {/* Two marks on the circle, in opposite corners so neither can be read
            as the other: a booked reminder above, the VIP pill below. Symbols
            only — this is a list to be scanned, and a word on a 34px circle is a
            word nobody reads; both are spelled out in the drawer.
            (上次會面 was tried here as a coloured ring and taken back out — on
            top of the gender ring already on the circle it just looked busy. It
            stays a dot-and-label chip on the row's status line, where it reads
            with its own number beside it.) */}
        <span
          data-testid="row-avatar"
          className={`clientv2-row-avatar clientv2-av-${avatarGenderClass(client, adviserEmail)}`}
        >
          {name.charAt(0)}
        </span>
        {/* Above the circle, opposite the VIP pill below it. */}
        {remindMark && (
          <span data-testid="row-remind" data-mark={remindMark} className={`clientv2-av-remind is-${remindMark}`}>
            <BellRing size={13} aria-hidden="true" />
          </span>
        )}
        {tier && (
          <span data-testid="row-tier" className={`clientv2-row-tier tier-${tier.cls}`}>
            {tierLabel}
          </span>
        )}
      </span>

      {/* Identity: the name, and only what is inseparable from it. Wrapper only
          — it is `display: contents` above 600px, so every child stays a direct
          flex item of the row exactly as before. Below 600px this is the row's
          first line, sharing it with the avatar and the shortcut cluster. */}
      <span className="clientv2-row-identity">
        <span aria-hidden="true" data-testid="row-name" className="clientv2-row-name">
          {name}
        </span>
        {nickname && (
          <span aria-hidden="true" className="clientv2-row-nickname">
            , {nickname}
          </span>
        )}
        {recruiting && (
          // V1 marks a recruitment lead with the 💼 emoji itself
          // (ClientEntry.js:739-747), which is why it is in colour. A line icon
          // read as a different marker, so use the same glyph.
          <span
            role="img"
            data-testid="row-recruit"
            className="clientv2-row-recruit"
            aria-label={intl.formatMessage({ id: 'recruit-lead' })}
            title={intl.formatMessage({ id: 'recruit-lead' })}
          >
            💼
          </span>
        )}
      </span>

      <span aria-hidden="true" className="clientv2-row-spacer" />

      {/* Everything else about the client, in one group: 家庭 first, then the
          status chips. Below 600px this is the row's second line; above it the
          wrapper is `display: contents` and these are the same flex items the
          row always had.

          🏠 family sits here rather than beside the name because the name is
          the one thing a row cannot do without — with both on the first line a
          long family chip pushed 「Eric Wan」 down to 「Eric …」, which is the
          wrong half of the row to spend width on.

          The group always renders, so every row is the same height; a client
          with nothing at all to show carries the 「補資料」 nudge instead of a
          blank line (only 18% of clients have a valid C.P.I., and the factsheet
          is what unlocks it). */}
      <span className="clientv2-row-meta" data-testid="row-meta">
        {/* 階段 leads the meta line in 招募模式, because it is the one fact that
            makes a candidate row readable at a glance — 潛力 and the C.P.I. dot
            are about a policy holder, and where someone is between 未開始 and
            已入職 is what the whole mode is sorted, counted and tabbed by.

            It is also the control: tapping it changes the stage. A pill that
            SHOWS the answer and changes it beats a fourth icon that only
            changes it, which is why the shortcut cluster stayed at three. */}
        {recruitMode && onRecruitStage && (
          <span
            role="button"
            tabIndex={canSaveStage ? 0 : -1}
            aria-disabled={canSaveStage ? undefined : true}
            title={canSaveStage ? undefined : intl.formatMessage({ id: 'clientv2-notes-no-factsheet' })}
            data-testid="row-stage"
            data-stage={stage || 'unset'}
            className={`clientv2-row-stage${canSaveStage ? '' : ' is-disabled'}`}
            aria-label={intl.formatMessage(
              { id: 'clientv2-recruit-row-stage' },
              {
                stage: intl.formatMessage({
                  id: stage ? `clientv2-recruit-stage-${stage}` : 'clientv2-recruit-stage-unset'
                })
              }
            )}
            style={
              stage
                ? ({ '--clientv2-stagetint': `${RECRUIT_STAGE_COLORS[stage]}26` } as React.CSSProperties)
                : undefined
            }
            onClick={event => {
              event.stopPropagation();
              if (canSaveStage) onRecruitStage(client, event.currentTarget as HTMLElement);
            }}
            onKeyDown={event => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              if (canSaveStage) onRecruitStage(client, event.currentTarget as HTMLElement);
            }}
          >
            {stage ? (
              <RecruitStageDot stage={stage} />
            ) : (
              <i
                className="clientv2-dot clientv2-stagedot is-hollow"
                style={{ '--clientv2-stagedot': '#cbd5e1' } as React.CSSProperties}
                aria-hidden="true"
              />
            )}
            {intl.formatMessage({ id: stage ? `clientv2-recruit-stage-${stage}` : 'clientv2-recruit-stage-unset' })}
            <ChevronDown size={11} aria-hidden="true" />
          </span>
        )}
        {families.length > 0 && (
          <span
            role={onFamilyFilter ? 'button' : undefined}
            tabIndex={onFamilyFilter ? 0 : undefined}
            aria-hidden={onFamilyFilter ? undefined : 'true'}
            aria-label={
              onFamilyFilter ? `${intl.formatMessage({ id: 'clientv2-family-tree' })}: ${familyLabel}` : undefined
            }
            title={familyLabel}
            data-testid="row-family"
            className="clientv2-row-family"
            onClick={event => {
              if (!onFamilyFilter) return;
              event.stopPropagation();
              onFamilyFilter(families);
            }}
            onKeyDown={event => {
              if (!onFamilyFilter || (event.key !== 'Enter' && event.key !== ' ')) return;
              event.preventDefault();
              event.stopPropagation();
              onFamilyFilter(families);
            }}
          >
            <House size={12} />
            {familyLabel}
          </span>
        )}
        {elapsed && (
          <span aria-hidden="true" data-testid="row-elapsed" className="clientv2-row-elapsed">
            <i className="clientv2-dot" style={{ backgroundColor: elapsed.color }} />
            {elapsed.label}
          </span>
        )}
        {/* 招募模式下,這個位置換成候選人評估 —— 和 C.P.I. 一樣的讀法:一個
            標籤加一點顏色,高/中/低三段。
            C.P.I. 量的是一個保單持有人值不值得跟進,答的是另一場對話 —— 一列
            候選人旁邊掛著「潛力 ●」,講的是他作為客戶有多好,而這份名單問的是
            他適不適合入行。兩者剛好都是三段式的紅黃綠,擺在同一個位置最容易被
            讀成同一件事,所以是換掉而不是並排。
            按下去開評分表,和旁邊那個雷達捷徑同一個目的地:晶片顯示答案,也是
            改答案的地方(和階段藥丸同一套)。 */}
        {recruitMode && recruitScore && recruitScoreState !== 'unrated' && (
          <span
            role={onRecruitSurface ? 'button' : undefined}
            tabIndex={onRecruitSurface ? 0 : undefined}
            aria-hidden={onRecruitSurface ? undefined : 'true'}
            aria-label={
              onRecruitSurface
                ? `${intl.formatMessage({ id: 'clientv2-recruit-radar-title' })} · ${recruitScoreDetail}`
                : undefined
            }
            title={`${intl.formatMessage({ id: 'clientv2-recruit-radar-title' })} · ${recruitScoreDetail}`}
            data-testid="row-recruit-score-chip"
            data-tier={recruitScoreState}
            className={`clientv2-row-cpi clientv2-row-recruit-score is-${recruitScoreState}`}
            onClick={event => {
              if (!onRecruitSurface) return;
              event.stopPropagation();
              onRecruitSurface(client, 'score');
            }}
            onKeyDown={event => {
              if (!onRecruitSurface || (event.key !== 'Enter' && event.key !== ' ')) return;
              event.preventDefault();
              event.stopPropagation();
              onRecruitSurface(client, 'score');
            }}
          >
            {recruitScoreLabel}
            <i className="clientv2-dot" />
          </span>
        )}
        {!recruitMode && cpi && (
          // 「潛力」 and a colour, not 「潛力 一般」. The three bands are a traffic
          // light, and a list is scanned rather than read: spelling the band out
          // beside its own dot made this the widest chip on the status line in
          // order to say the same thing twice. The word that stayed is the
          // LABEL, so it leads and the dot follows as its value — the drawer's
          // 潛力 tag reads in that order already (label · dot · band), and this
          // is that tag minus the band.
          // What the colour MEANS was never on the row at all, and is now one
          // tap away: the chip opens the same C.P.I. explainer the client-base
          // indicator in the command bar does. The band itself stays in words
          // for a screen reader, twice over — the row's composite label and
          // this control's own name.
          <span
            role={onCpiInfo ? 'button' : undefined}
            tabIndex={onCpiInfo ? 0 : undefined}
            aria-hidden={onCpiInfo ? undefined : 'true'}
            aria-label={onCpiInfo ? cpiLabel : undefined}
            title={cpiTitle}
            data-testid="row-cpi"
            // Lets the phone stylesheet and any future band-specific rule read
            // the band without re-deriving it from the inline colour.
            data-cpi-level={String(client.scoreLevel)}
            className="clientv2-row-cpi"
            onClick={event => {
              if (!onCpiInfo) return;
              event.stopPropagation();
              onCpiInfo();
            }}
            onKeyDown={event => {
              if (!onCpiInfo || (event.key !== 'Enter' && event.key !== ' ')) return;
              event.preventDefault();
              event.stopPropagation();
              onCpiInfo();
            }}
          >
            {potentialWord}
            <i className="clientv2-dot" style={{ backgroundColor: cpi.color }} />
          </span>
        )}
        {tracking && (
          <span
            aria-hidden="true"
            data-testid="row-tracking"
            className="clientv2-row-tracking"
            // `1f` = ~12% alpha on the same TRACKING_COLORS hex, so the fill is the
            // opportunity colour rather than a neutral grey. All four values are
            // 6-digit hex, so the 8-digit form is safe. Text uses the darker
            // textColor shade — the pastel border/fill colour is too washed out
            // to read as text against its own near-white fill.
            style={{
              borderColor: tracking.color,
              color: tracking.textColor,
              backgroundColor: `${tracking.color}1f`
            }}
          >
            <i className="clientv2-dot" style={{ backgroundColor: tracking.color }} />
            <span className="clientv2-row-tracking-copy">{followingWord}</span>
          </span>
        )}
        {/* Nothing to report about this client yet. The row opens the drawer,
            whose 詳細資料 tile is where the missing fields are filled in, so
            this is a label on the way there rather than a control of its own —
            a second tap target inside a row that is itself a button buys
            nothing here. */}
        {/* In 招募模式 the question is whether the SCORE CHIP rendered, not
            whether a summary exists — `summariseRecruitScores` always returns
            one, so testing it kept this hint off every unrated candidate. */}
        {!elapsed && !(recruitMode ? recruitScoreState !== 'unrated' : cpi) && !tracking && families.length === 0 && (
          <span aria-hidden="true" data-testid="row-fill-in" className="clientv2-row-fill-in">
            <Plus size={11} />
            {intl.formatMessage({ id: 'clientv2-row-fill-in' })}
          </span>
        )}
      </span>

      {recruitMode && onRecruitSurface && (
        // 招募模式's three, in the same seats: prepare the conversation, record
        // it, rate them. 救命紙 and 智能摘要 are not withheld out of caution —
        // they are about a policy holder, and this row is a candidate.
        //
        // The stage pill beside the name is the fourth control and the most
        // used of all, which is why it is a pill rather than a fourth icon:
        // it SHOWS the answer as well as changing it (see ClientRowStage).
        <span className="clientv2-row-shortcuts">
          <RowShortcut
            testId="row-recruit-idea"
            label={intl.formatMessage({ id: 'clientv2-recruit-idea' })}
            onActivate={() => onRecruitSurface(client, 'idea')}
          >
            <Compass size={22} aria-hidden="true" />
          </RowShortcut>
          <RowShortcut
            testId="row-recruit-note"
            // 招募筆記 IS 會面筆記 — one dialog, relabelled for the conversation
            // being recorded (design doc §4.2). So it keeps the capability gate
            // the business shortcut has, because it is the same dialog.
            //
            // And it enters at the recorder, with the same 🎤 badge, for the
            // same reason the business row does: from a row the ask is 「記下
            // 剛才那段對話」, and the fastest way to say it is out loud. A
            // recruitment conversation is not a different kind of ask.
            label={intl.formatMessage({ id: 'clientv2-recruit-note-voice' })}
            corner={<Mic size={9} aria-hidden="true" />}
            hint={restrictionText}
            disabled={!capabilities.has('meeting-note')}
            onRefuse={() => onRestricted?.(client, 'meeting-note')}
            onActivate={() => onQuickAction?.('meeting-record', client)}
          >
            <NotebookPen size={22} aria-hidden="true" />
          </RowShortcut>
          <RowShortcut
            testId="row-recruit-score"
            // 候選人評估, which is what the section is called where it lands —
            // the header pill's 「評估」 is an abbreviation that only works
            // beside a number, and this label is the only part a screen reader
            // hears. Same rule as 救命紙 · 修改: a shortcut names its
            // destination.
            label={intl.formatMessage({ id: 'clientv2-recruit-radar-title' })}
            onActivate={() => onRecruitSurface(client, 'score')}
          >
            <Radar size={22} aria-hidden="true" />
          </RowShortcut>
        </span>
      )}

      {!recruitMode && onQuickAction && (
        // Keep the operable shortcut cluster at the far right of every row:
        // status facts remain grouped together first, then contact,
        // portfolio and smart idea sit immediately before the drawer
        // affordance. Sized up from V1's cramped 15px glyphs so the row is
        // easy to tap by hand.
        <span className="clientv2-row-shortcuts">
          <RowShortcut
            testId="row-sos"
            // 「救命紙 · 修改」, not the drawer card's plain 救命紙: same icon and
            // same feature, but a name that stops at 救命紙 promises the card's
            // menu and delivers an editor. The second word is the whole
            // difference, and it is the only part a screen reader can hear —
            // the ✏️ badge below is the same sentence for everyone else.
            label={intl.formatMessage({ id: 'clientv2-row-sos-edit' })}
            hint={restrictionText}
            corner={<Pencil size={9} aria-hidden="true" />}
            // Same capability as the drawer's 救命紙 card, and the same page in
            // the end — but this one lands ON the sheet's editor rather than on
            // the export menu that offers it. A row shortcut that opens a menu
            // has spent the tap it was there to save (see goSosEdit).
            disabled={!capabilities.has('export')}
            onRefuse={() => onRestricted?.(client, 'export')}
            onActivate={() => onQuickAction('sos-edit', client)}
          >
            <LifeBuoy size={22} aria-hidden="true" />
          </RowShortcut>
          <RowShortcut
            testId="row-meeting-note"
            // 「會面筆記 · 語音」, on the same terms as 救命紙 · 修改 above.
            label={intl.formatMessage({ id: 'clientv2-row-meeting-voice' })}
            hint={restrictionText}
            corner={<Mic size={9} aria-hidden="true" />}
            // Same dialog the drawer's 會面筆記 card opens, entered at the
            // recorder: from a row the ask is 「記下這次會面」, and the fastest
            // way to say it is out loud.
            disabled={!capabilities.has('meeting-note')}
            onRefuse={() => onRestricted?.(client, 'meeting-note')}
            onActivate={() => onQuickAction('meeting-record', client)}
          >
            <NotebookPen size={22} aria-hidden="true" />
          </RowShortcut>
          <RowShortcut
            testId="row-smart-idea"
            label={intl.formatMessage({ id: 'clientv2-smart-idea' })}
            hint={restrictionText}
            disabled={!capabilities.has('smart-idea')}
            onRefuse={() => onRestricted?.(client, 'smart-idea')}
            onActivate={() => onQuickAction('smart-idea', client)}
          >
            <Lightbulb size={22} aria-hidden="true" />
          </RowShortcut>
        </span>
      )}

      <ChevronRight aria-hidden="true" className="clientv2-row-icon" size={17} />
    </button>
  );
};

export default injectIntl(ClientRow);
